import {
  Delete,
  FilterList as FilterListIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  PlusOne,
  SearchOff,
} from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from '@mui/icons-material/Save';
import ShowChartIcon from "@mui/icons-material/ShowChart";
import {
  Badge,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useErrorHandler } from "../../../hooks";
import { MonitoringRecordForm as MonitoringRecordFormType } from "../../../types/forms/capture";
import { MonitoringResponse } from "../../../types/model/monitoring";
import { VariableResponse } from "../../../types/model/variable";
import { formatTimestamp } from "../../../utils";
import GenericFilter, { FilterField } from "../../General/GenericFilter";
import { MonitoringRecordsImportDialog } from "../../Imports/MonitoringRecords/MonitoringRecordsImportDialog";
import MonitoringRecordForm from "./MonitoringRecordForm";

interface FilterOptions {
  variableId?: number;
  startDate?: string;
  endDate?: string;
}

interface RowProps {
  row: MonitoringResponse;
  open: boolean;
  canEdit: boolean;
  onToggle: () => void;
  onUpdate: (
    monitoringId: number,
    values: MonitoringRecordFormType,
  ) => Promise<void>;
  onDelete: (monitoringId: number) => Promise<void>;
}

const Row = ({ row, open, canEdit, onToggle, onUpdate, onDelete }: RowProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const handleError = useErrorHandler();
  const formId = `form-${row.id}`;

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSave = async (values: MonitoringRecordFormType) => {
    try {
      setIsSaving(true);
      await onUpdate(row.id, values);
      setIsEditing(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete(row.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };

  return (
    <React.Fragment>
      <TableRow
        onClick={onToggle}
        sx={{ cursor: "pointer", "& > *": { borderBottom: "unset" } }}
      >
        <TableCell>
          {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </TableCell>
        <TableCell component="th" scope="row">
          {formatTimestamp(row.measuredAt)}
        </TableCell>
        <TableCell>{row.variableName}</TableCell>
        <TableCell>
          {row.flag} {row.value} {row.variableUnit}
        </TableCell>
        {canEdit && (
          <TableCell align="right">
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
              {isEditing ? (
                <>
                  <Tooltip title={t("generic.cancel")}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                      disabled={isSaving}
                    >
                      <EditOffIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("generic.save")}>
                    <IconButton
                      size="small"
                      color="success"
                      type="submit"
                      form={formId}
                      disabled={isSaving}
                    >
                      {isSaving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip title={t("generic.edit")}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!open) onToggle();
                        setIsEditing(true);
                      }}
                      disabled={isDeleting}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("generic.delete")}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                      disabled={isDeleting}
                    >
                      {isDeleting ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </TableCell>
        )}
      </TableRow>
      <TableRow
        sx={open ? { backgroundColor: theme.palette.action.hover } : {}}
      >
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={canEdit ? 5 : 4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <MonitoringRecordForm
                monitoringRecordData={row}
                formId={formId}
                isEditing={isEditing}
                isSaving={isSaving}
                isDeleting={isDeleting}
                onSubmit={handleSave}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

interface CaptureMonitoringRecordsProps {
  captureId: number;
  monitoringRecordsData: MonitoringResponse[];
  canEdit: boolean;
  variables: VariableResponse[];
  filters: FilterOptions;
  onApplyFilters: (filters: FilterOptions) => void;
  onCreate: (values: MonitoringRecordFormType) => Promise<void>;
  onUpdate: (
    monitoringId: number,
    values: MonitoringRecordFormType,
  ) => Promise<void>;
  onDelete: (monitoringId: number) => Promise<void>;
  isChartMaximized: boolean;
  toggleMapMaximization: () => void;
  page: number;
  onPageChange: (page: number) => void;
  onRequestRefresh?: () => void;
}

export default function CaptureMonitoringRecords({
  captureId,
  monitoringRecordsData,
  canEdit,
  variables,
  filters,
  onApplyFilters,
  onCreate,
  onUpdate,
  onDelete,
  isChartMaximized,
  toggleMapMaximization,
  page,
  onPageChange,
  onRequestRefresh,
}: Readonly<CaptureMonitoringRecordsProps>) {
  const { t } = useTranslation();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterAnchorEl, setFilterAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [openRowId, setOpenRowId] = useState<number | null | string>(null);
  const [importOpen, setImportOpen] = useState(false);

  const newFormId = "form-new";
  const [isCreating, setIsCreating] = useState(false);

  const handleToggleFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(filterAnchorEl ? null : event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const isFilterOpen = Boolean(filterAnchorEl);

  const filterFields: FilterField[] = useMemo(
    () => [
      {
        name: "variableId",
        label: t("captures.capture.monitoring.variable"),
        type: "autocomplete",
        options: variables.map((v) => ({ value: v.id, label: v.name })),
      },
      {
        name: "startDate",
        label: t("captures.capture.monitoring.start-date"),
        type: "date",
      },
      {
        name: "endDate",
        label: t("captures.capture.monitoring.end-date"),
        type: "date",
      },
    ],
    [variables, t],
  );

  const activeFilters = useMemo(() => {
    const list: { key: keyof FilterOptions; label: string; value: string }[] =
      [];
    if (filters.variableId) {
      const variable = variables.find((v) => v.id === filters.variableId);
      if (variable) {
        list.push({
          key: "variableId",
          label: t("captures.capture.monitoring.variable"),
          value: variable.name,
        });
      }
    }
    if (filters.startDate) {
      list.push({
        key: "startDate",
        label: t("captures.capture.monitoring.start-date"),
        value: formatTimestamp(filters.startDate),
      });
    }
    if (filters.endDate) {
      list.push({
        key: "endDate",
        label: t("captures.capture.monitoring.end-date"),
        value: formatTimestamp(filters.endDate),
      });
    }
    return list;
  }, [filters, variables, t]);

  const handleClearFilters = () => {
    onApplyFilters({});
  };

  const handleDeleteFilter = (key: keyof FilterOptions) => {
    onApplyFilters({ ...filters, [key]: undefined });
  };

  const visibleRows = monitoringRecordsData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100%" }} variant="h5">
          {t("captures.capture.monitoring.title")}
        </Typography>

        <Tooltip
          title={
            isChartMaximized ? t("generic.minimize") : t("generic.maximize")
          }
        >
          <IconButton onClick={toggleMapMaximization}>
            <ShowChartIcon color={isChartMaximized ? "primary" : undefined} />
          </IconButton>
        </Tooltip>

        <Tooltip title={t("generic.filter")}>
          <IconButton onClick={handleToggleFilter}>
            <Badge badgeContent={activeFilters.length} color="primary">
              <FilterListIcon color={isFilterOpen ? "primary" : undefined} />
            </Badge>
          </IconButton>
        </Tooltip>
        
        {canEdit && (
          <>
            <Tooltip title={t("captures.capture.monitoring.add-new")}>
              <IconButton
                onClick={() => setOpenRowId(openRowId === "new" ? null : "new")}
              >
                <PlusOne />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("imports.import-dialog.title")}>
              <IconButton onClick={() => setImportOpen(true)}>
                <CloudUploadIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Toolbar>

      {activeFilters.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
        >
          {activeFilters.map((filter) => (
            <Chip
              key={filter.key}
              label={`${filter.label}: ${filter.value}`}
              onDelete={() => handleDeleteFilter(filter.key)}
              size="small"
              variant="outlined"
            />
          ))}
        </Stack>
      )}

      <Popover
        open={isFilterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            sx: { p: 3, width: 350 },
          },
        }}
      >
        <GenericFilter
          fields={filterFields}
          filters={filters}
          onFiltersChange={(newFilters) =>
            onApplyFilters(newFilters as FilterOptions)
          }
          onApplyFilters={(appliedFilters) =>
            onApplyFilters((appliedFilters as FilterOptions) ?? filters)
          }
          onClearFilters={handleClearFilters}
        />
      </Popover>

      <Collapse in={!isChartMaximized} timeout="auto">
        <TableContainer>
          <Table aria-label="collapsible table" sx={{ tableLayout: "fixed" }}>
            {openRowId !== "new" && (
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "5%" }} />
                  <TableCell>
                    {t("captures.capture.monitoring.measured-at")}
                  </TableCell>
                  <TableCell>
                    {t("captures.capture.monitoring.variable")}
                  </TableCell>
                  <TableCell>
                    {t("captures.capture.monitoring.value")}
                  </TableCell>
                  {canEdit && (
                    <TableCell sx={{ width: "10%" }} align="right">
                      {t("generic.actions")}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
            )}

            <TableBody>
              {openRowId === "new" && (
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={canEdit ? 5 : 4}
                  >
                    <Collapse in={true} timeout="auto" unmountOnExit>
                      <Paper sx={{ p: 3, m: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {t("captures.capture.monitoring.add-new")}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title={t("generic.cancel")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setOpenRowId(null)}
                              disabled={isCreating}
                            >
                              <EditOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("generic.save")}>
                            <IconButton
                              size="small"
                              color="success"
                              type="submit"
                              form={newFormId}
                              disabled={isCreating}
                            >
                              {isCreating ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                        <MonitoringRecordForm
                          formId={newFormId}
                          isEditing={true}
                          isSaving={isCreating}
                          isDeleting={false}
                          onSubmit={async (values) => {
                            try {
                              setIsCreating(true);
                              await onCreate(values);
                              setOpenRowId(null);
                            } finally {
                              setIsCreating(false);
                            }
                          }}
                        />
                      </Paper>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}

              {visibleRows.length === 0 && openRowId !== "new" && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 5 : 4} align="center" sx={{ py: 10 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        opacity: 0.6,
                      }}
                    >
                      <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                      <Typography variant="h6">
                        {t("generic.no-records")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {visibleRows.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  open={openRowId === row.id}
                  canEdit={canEdit}
                  onToggle={() =>
                    setOpenRowId(openRowId === row.id ? null : row.id)
                  }
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={monitoringRecordsData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => onPageChange(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            onPageChange(0);
          }}
        />
      </Collapse>

      <MonitoringRecordsImportDialog
        captureId={captureId}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={() => {
          setImportOpen(false);
          onRequestRefresh?.();
        }}
      />
    </Paper>
  );
}
